import React, { useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import { Select } from 'antd';
import type { SelectProps as AntSelectProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { deriveScopedComponentId, mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { useDictOptions, useRemoteOptions } from '../../base/hooks/useApiService';
import { findDictOptionByValue } from '../../config';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';

const VALIDATE_DEBOUNCE_MS = 200;

/** 与 `dataSetField` 拼接：`${dataSetField}${SELECTED_FIELD_SUFFIX}`，如 `leaderId_selected`、`[0].userId_selected` */
const SELECTED_FIELD_SUFFIX = '_selected';

const SELECT_EXTRA_SKIP = [
  'optionsCfg',
  'enableDict',
  'dictType',
  'autoSelectFirst',
  'token',
] as const;

/** 自定义 `onChange` 时不写入 store，也不维护 `_selected`（与主字段行为一致） */
export type AndSelectSelectedState =
  | null
  | { key: any; label: string }
  | { key: any[]; label: string[] };

function formatSelectLabel(label: unknown): string {
  if (label == null) return '';
  if (typeof label === 'string' || typeof label === 'number' || typeof label === 'boolean') {
    return String(label);
  }
  if (React.isValidElement(label)) {
    const ch = (label.props as { children?: unknown })?.children;
    if (typeof ch === 'string' || typeof ch === 'number') return String(ch);
    if (Array.isArray(ch)) return ch.map(formatSelectLabel).join('');
  }
  return String(label);
}

function selectedSnapshotEqual(a: AndSelectSelectedState, b: AndSelectSelectedState): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const ak = (a as { key: unknown }).key;
  const bk = (b as { key: unknown }).key;
  const al = (a as { label: unknown }).label;
  const bl = (b as { label: unknown }).label;
  if (Array.isArray(ak) && Array.isArray(bk) && Array.isArray(al) && Array.isArray(bl)) {
    if (ak.length !== bk.length || al.length !== bl.length) return false;
    return ak.every((x, i) => x === bk[i]) && al.every((x, i) => x === bl[i]);
  }
  if (Array.isArray(ak) || Array.isArray(bk)) return false;
  return ak === bk && al === bl;
}

/**
 * 根据当前 value、antd 回调的 option、选项列表解析 `{ key, label }`（多选为并行数组）。
 * `option` 可省略，仅依赖 `finalOptions` 反查（用于选项异步到达后的同步）。
 */
function resolveSelectSelectedSnapshot(
  value: any,
  option: any,
  finalOptions: { value?: unknown; label?: unknown }[],
  labelInValue?: boolean,
  mode?: AntSelectProps['mode']
): AndSelectSelectedState {
  const isMulti =
    mode === 'multiple' || mode === 'tags' || Array.isArray(value);

  if (!isMulti) {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    if (
      labelInValue &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'value' in value
    ) {
      const v = value as { value: any; label?: unknown };
      return { key: v.value, label: formatSelectLabel(v.label) };
    }
    const opt =
      option && !Array.isArray(option) ? option : findDictOptionByValue(finalOptions, value);
    const labRaw = opt?.label ?? (opt as any)?.children;
    const label = formatSelectLabel(
      labRaw !== undefined && labRaw !== null
        ? labRaw
        : findDictOptionByValue(finalOptions, value)?.label ?? String(value)
    );
    return { key: value, label };
  }

  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  if (
    labelInValue &&
    value[0] &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'value' in value[0]
  ) {
    const keys = (value as { value: any; label?: unknown }[]).map((v) => v.value);
    const labels = (value as { value: any; label?: unknown }[]).map((v) =>
      formatSelectLabel(v.label)
    );
    return { key: keys, label: labels };
  }
  const opts = Array.isArray(option) ? option : [];
  const keys = value;
  const labels = keys.map((k: any, i: number) => {
    const o = opts[i];
    const labRaw = o?.label ?? o?.children;
    if (labRaw !== undefined && labRaw !== null) return formatSelectLabel(labRaw);
    return formatSelectLabel(findDictOptionByValue(finalOptions, k)?.label ?? String(k));
  });
  return { key: keys, label: labels };
}

// 选项配置接口
interface OptionsCfg {
  url?: string;
  codeKey?: string;
  labelKey?: string;
  /** 请求时的查询参数，如 { pageSize: 100, status: 0 }，与 url 一起传给远程接口 */
  params?: Record<string, any>;
  /**
   * 响应中选项数组所在字段名。不传时：若响应/response.data 本身为数组则直接用，否则取 list 或 records。
   * 指定后从 response/response.data 的该字段取数组，如 "children" 表示从 data.children 取选项列表。
   */
  field?: string;
}

/**
 * AndSelect Props 接口
 */
export interface AndSelectProps extends Omit<AntSelectProps, 'onChange'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 是否启用字典 */
  enableDict?: boolean;
  /** 字典类型 */
  dictType?: string;
  /** 远程选项配置 */
  optionsCfg?: OptionsCfg;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 变更动作 */
  changeAction?: string;
  /** 自定义变更处理函数 */
  onChange?: (value: any, option: any) => void;
  /** 当前未选中时，options 有值后是否自动选中第一个 */
  autoSelectFirst?: boolean;
  /** 迭代器内当前行序号，供 changeAction 使用 @index 占位符 */
  index?: number;
}

/**
 * AndSelect 组件
 * 封装 Ant Design Select，支持字典、远程数据和动作解析
 */
export const AndSelect: React.FC<AndSelectProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  enableDict,
  dictType,
  optionsCfg,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  onChange,
  autoSelectFirst = false,
  options: propOptions,
  defaultValue,
  value: propValue,
  index,
  showArrow: _showArrow,
  remoteUrl: _remoteUrl,
  searchAction: _searchAction,
  selectAction: _selectAction,
  deselectAction: _deselectAction,
  dictCode: _dictCode,
  dropdownMatchSelectWidth: _dropdownMatchSelectWidth,
  ...restProps
}: AndSelectProps & Record<string, any>) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctx = useContext(DataSetInheritContext);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField ? s.getDataSet(dataSetKey, effectiveDataSetField) : undefined
  );

  // 优先使用 propsStore 中的 optionsCfg，以便 setProps 更新配置时触发重新请求
  const effectiveOptionsCfg = useMemo(
    () => (storeProps?.optionsCfg != null ? storeProps.optionsCfg : optionsCfg),
    [storeProps?.optionsCfg, optionsCfg]
  );

  const { options: dictOptions, loading: dictLoading } = useDictOptions(
    _dictCode || (enableDict ? dictType : undefined)
  );
  const { options: remoteOptions, loading: remoteLoading } = useRemoteOptions(
    effectiveOptionsCfg?.url,
    {
      valueKey: effectiveOptionsCfg?.codeKey || 'value',
      labelKey: effectiveOptionsCfg?.labelKey || 'label',
      immediate: !!effectiveOptionsCfg?.url,
      params: effectiveOptionsCfg?.params,
      field: effectiveOptionsCfg?.field,
    }
  );
  const isLoading = dictLoading || remoteLoading;

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  const rawDisplayValue = useMemo(() => {
    if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
      return storeValue;
    }
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue ?? defaultValue;
  }, [storeValue, storeProps?.value, propValue, defaultValue]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const selectDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>, SELECT_EXTRA_SKIP),
    [mergedProps],
  );

  // 计算最终选项：storeProps.options > 远程 > 字典 > 配置
  const finalOptions = useMemo(() => {
    if (storeProps?.options && Array.isArray(storeProps.options)) return storeProps.options;
    if (effectiveOptionsCfg?.url && remoteOptions.length > 0) return remoteOptions;
    if ((_dictCode || (enableDict && dictType)) && dictOptions.length > 0) return dictOptions;
    return propOptions || [];
  }, [storeProps?.options, effectiveOptionsCfg?.url, remoteOptions, _dictCode, enableDict, dictType, dictOptions, propOptions]);

  // 字典 value 多为 string，实际数据可能为 number，需用选项中的 value 以保证 antd Select 正确选中
  const displayValue = useMemo(() => {
    if (finalOptions.length === 0) return rawDisplayValue;
    if (Array.isArray(rawDisplayValue)) {
      return rawDisplayValue.map((v) => findDictOptionByValue(finalOptions, v)?.value ?? v);
    }
    const matched = findDictOptionByValue(finalOptions, rawDisplayValue);
    return matched?.value ?? rawDisplayValue;
  }, [rawDisplayValue, finalOptions]);

  const selectedField = effectiveDataSetField ? `${effectiveDataSetField}${SELECTED_FIELD_SUFFIX}` : '';
  const autoSelectSignatureRef = useRef<string | null>(null);

  const handleChange = useCallback(
    (value: any, option: any) => {
      if (onChange) {
        onChange(value, option);
        return;
      }
      if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, value);
        const snapshot = resolveSelectSelectedSnapshot(
          value,
          option,
          finalOptions,
          mergedProps.labelInValue,
          mergedProps.mode
        );
        setDataSet(dataSetKey, selectedField, snapshot);
      }
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(value);
        }, VALIDATE_DEBOUNCE_MS);
      }
      if (changeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, value, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      finalOptions,
      mergedProps.labelInValue,
      mergedProps.mode,
      selectedField,
      ctx,
    ]
  );

  /** 选项异步加载或外部改主字段时，补全 `_selected` 的 label */
  useEffect(() => {
    if (onChange || !dataSetKey || !effectiveDataSetField || !selectedField) return;
    const snapshot = resolveSelectSelectedSnapshot(
      rawDisplayValue,
      undefined,
      finalOptions,
      mergedProps.labelInValue,
      mergedProps.mode
    );
    const current = useAndStore.getState().getDataSet(dataSetKey, selectedField) as AndSelectSelectedState;
    if (selectedSnapshotEqual(current, snapshot)) return;
    setDataSet(dataSetKey, selectedField, snapshot);
  }, [
    onChange,
    dataSetKey,
      effectiveDataSetField,
    selectedField,
    rawDisplayValue,
    finalOptions,
    mergedProps.labelInValue,
    mergedProps.mode,
    setDataSet,
  ]);

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  const autoSelectEnabled = storeProps?.autoSelectFirst ?? autoSelectFirst;
  const isCurrentValueEmpty = useMemo(() => {
    if (Array.isArray(rawDisplayValue)) return rawDisplayValue.length === 0;
    if (rawDisplayValue === undefined || rawDisplayValue === null || rawDisplayValue === '') return true;
    if (
      mergedProps.labelInValue &&
      rawDisplayValue &&
      typeof rawDisplayValue === 'object' &&
      !Array.isArray(rawDisplayValue) &&
      'value' in (rawDisplayValue as Record<string, unknown>)
    ) {
      const lv = (rawDisplayValue as { value?: unknown }).value;
      return lv === undefined || lv === null || lv === '';
    }
    return false;
  }, [rawDisplayValue, mergedProps.labelInValue]);

  useEffect(() => {
    if (!autoSelectEnabled || finalOptions.length === 0 || !isCurrentValueEmpty) {
      autoSelectSignatureRef.current = null;
      return;
    }

    const valueFieldName = mergedProps.fieldNames?.value || 'value';
    const labelFieldName = mergedProps.fieldNames?.label || 'label';
    const firstOption = finalOptions[0] as Record<string, any> | undefined;
    if (!firstOption) return;

    const firstValue = firstOption[valueFieldName] ?? firstOption.value;
    if (firstValue === undefined || firstValue === null || firstValue === '') return;

    const signature = `${dataSetKey ?? ''}:${effectiveDataSetField ?? ''}:${String(firstValue)}`;
    if (autoSelectSignatureRef.current === signature) return;
    autoSelectSignatureRef.current = signature;

    const isMultiMode =
      mergedProps.mode === 'multiple' || mergedProps.mode === 'tags' || Array.isArray(rawDisplayValue);
    const firstLabel = firstOption[labelFieldName] ?? firstOption.label;

    if (mergedProps.labelInValue) {
      const nextLabelInValue = isMultiMode
        ? [{ value: firstValue, label: firstLabel }]
        : { value: firstValue, label: firstLabel };
      handleChange(nextLabelInValue, isMultiMode ? [firstOption] : firstOption);
      return;
    }

    const nextValue = isMultiMode ? [firstValue] : firstValue;
    handleChange(nextValue, isMultiMode ? [firstOption] : firstOption);
  }, [
    autoSelectEnabled,
    dataSetKey,
    effectiveDataSetField,
    finalOptions,
    handleChange,
    isCurrentValueEmpty,
    mergedProps.fieldNames?.label,
    mergedProps.fieldNames?.value,
    mergedProps.labelInValue,
    mergedProps.mode,
    rawDisplayValue,
  ]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  // 过滤选项
  const filterOption = useCallback((input: string, option: any) => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  }, []);

  return (
    <div style={{ width: '100%' }}>
      <Select
        {...(selectDomProps as AntSelectProps)}
        disabled={develop ? false : (selectDomProps.disabled as boolean | undefined)}
        open={develop ? false : undefined}
        value={displayValue}
        options={finalOptions}
        onChange={handleChange}
        onBlur={handleBlur}
        filterOption={filterOption}
        loading={isLoading}
        status={help ? ("error" as const) : undefined}
        style={{ width: '100%', ...(selectDomProps.style as React.CSSProperties) }}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndSelect;
